import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { emailApi, profileApi } from '../lib/api';

const isIdDoc = (d) => d.type === 'proof_of_id' || d.document_type === 'proof_of_id';
const isAddressDoc = (d) => d.type === 'proof_of_address' || d.document_type === 'proof_of_address';

/**
 * useIdDocuments
 * --------------
 * Everything about the user's ID / proof-of-address documents and the
 * "share automatically" permission, in one place:
 *
 *   • derived flags (has ID, has address doc, permission granted, ready)
 *   • upload + permission-toggle handlers (with their busy flags)
 *   • the "other requests are waiting for your ID" prompt that appears after an
 *     upload or a permission grant
 *
 * `currentSarId` is excluded from the pending-requests prompt so we don't
 * offer to send ID to the request the user is already looking at.
 */
export default function useIdDocuments({ currentSarId } = {}) {
  const { user, loadUser } = useAuth();

  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingAddress, setUploadingAddress] = useState(false);
  const [idPermission, setIdPermission] = useState(user?.id_sharing_permission || false);
  const [savingPermission, setSavingPermission] = useState(false);

  const [showPendingIdDialog, setShowPendingIdDialog] = useState(false);
  const [pendingIdRequests, setPendingIdRequests] = useState([]);
  const [sendingToAll, setSendingToAll] = useState(false);

  const idDocument = user?.id_documents?.find(isIdDoc);
  const addressDocument = user?.id_documents?.find(isAddressDoc);
  const hasIdDocuments = !!idDocument;
  const hasAddressDoc = !!addressDocument;
  const hasIdPermission = !!user?.id_sharing_permission;
  const isReadyForIdRequests = (hasIdDocuments || hasAddressDoc) && hasIdPermission;

  useEffect(() => {
    setIdPermission(user?.id_sharing_permission || false);
  }, [user?.id_sharing_permission]);

  const checkPendingIdRequests = async () => {
    try {
      const res = await emailApi.getPendingIdRequests();
      const pending = res.data.pending_id_requests || [];
      const otherPending = pending.filter((p) => p.sar_id !== currentSarId);
      if (otherPending.length > 0) {
        setPendingIdRequests(otherPending);
        setShowPendingIdDialog(true);
      }
    } catch {
      console.log('Could not check pending ID requests');
    }
  };

  const handleDocUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const setUploading = documentType === 'proof_of_id' ? setUploadingId : setUploadingAddress;
    setUploading(true);

    try {
      await profileApi.uploadIdDocument(file, documentType);
      toast.success(documentType === 'proof_of_id'
        ? 'ID uploaded. It is locked with your password.'
        : 'Address document uploaded. It is locked with your password.');
      await loadUser(); // refresh user to get updated documents

      // Check if there are other SARs waiting for ID
      setTimeout(() => checkPendingIdRequests(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not upload that. Try again in a moment.');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handlePermissionChange = async (checked) => {
    setSavingPermission(true);
    try {
      await profileApi.update({ id_sharing_permission: checked });
      setIdPermission(checked);
      await loadUser();
      toast.success(checked
        ? 'We will send your ID whenever an organisation asks for it.'
        : 'We will not send your ID without you.');

      // If permission just granted and has docs, check for pending requests
      if (checked && (hasIdDocuments || hasAddressDoc)) {
        setTimeout(() => checkPendingIdRequests(), 500);
      }
    } catch {
      toast.error('Could not save that. Try again in a moment.');
    } finally {
      setSavingPermission(false);
    }
  };

  const handleSendIdToAll = async () => {
    setSendingToAll(true);
    try {
      const res = await emailApi.sendIdToAllPending();
      const n = res.data.sent_count;
      toast.success(`Your ID was sent to ${n} organisation${n === 1 ? '' : 's'}.`);
      setShowPendingIdDialog(false);
      setPendingIdRequests([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send your ID. Try again in a moment.');
    } finally {
      setSendingToAll(false);
    }
  };

  return {
    // documents + derived flags
    idDocument,
    addressDocument,
    hasIdDocuments,
    hasAddressDoc,
    hasIdPermission,
    isReadyForIdRequests,
    // upload + permission
    uploadingId,
    uploadingAddress,
    idPermission,
    savingPermission,
    handleDocUpload,
    handlePermissionChange,
    // "send ID to other pending SARs" prompt
    showPendingIdDialog,
    setShowPendingIdDialog,
    pendingIdRequests,
    sendingToAll,
    handleSendIdToAll,
  };
}
